import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/authService';
import sessionService from '../../services/sessionService';
import { formatMoney } from '../../utils/money';
import EditProfileDialog from '../../components/EditProfileDialog/EditProfileDialog';
import ChangePasswordDialog from '../../components/ChangePasswordDialog/ChangePasswordDialog';
import {
  BookOpen,
  Calendar,
  Clock,
  CheckCircle,
  PlusCircle,
  ClipboardList,
  ArrowRight,
  User,
  Mail,
  Briefcase,
  Shield,
  Edit3,
  Key,
  AlertCircle,
  Euro,
  Receipt,
  Truck,
  Building2,
  Package,
  Users as UsersIcon
} from 'lucide-react';
import './DashboardPage.css';

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const { user, isAdmin, isResponsable, isLogistiqueCoordinator, isManager, isComptable, isFournisseur, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [accountingDashboard, setAccountingDashboard] = useState(null);
  const [pendingSessions, setPendingSessions] = useState([]);
  const [verificationSessions, setVerificationSessions] = useState([]);
  const [pendingValidationSessions, setPendingValidationSessions] = useState([]);
  const [pendingAdditionalFees, setPendingAdditionalFees] = useState([]);
  const [pendingRestockOrders, setPendingRestockOrders] = useState([]);
  const [recentDemandes, setRecentDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const statsResponse = await apiClient.get('/dashboard/stats/');
        setStats(statsResponse.data);

        if (isComptable) {
          try {
            const dashboardResp = await sessionService.accountingDashboard({ months: 6 });
            setAccountingDashboard(dashboardResp);
          } catch (e) {
            setAccountingDashboard(null);
          }
        }

        // Fetch last 10 sessions created by Responsable
        if (isResponsable) {
          const sessionsResponse = await apiClient.get('/sessions/?page_size=10&ordering=-created_at');
          setPendingSessions(sessionsResponse.data.results || sessionsResponse.data || []);
        }

        // Fetch sessions pending verification for Logistique Coordinator
        if (isLogistiqueCoordinator) {
          const sessionsResponse = await apiClient.get('/sessions/?page_size=10&status=VERIFICATION&ordering=-created_at');
          setVerificationSessions(sessionsResponse.data.results || sessionsResponse.data || []);
        }

        // Fetch sessions pending validation for Manager (status=VERIFIE)
        if (isManager) {
          const sessionsResponse = await apiClient.get('/sessions/?page_size=10&status=VERIFIE&ordering=-created_at');
          setPendingValidationSessions(sessionsResponse.data.results || sessionsResponse.data || []);
        }

        if (isLogistiqueCoordinator || isManager) {
          const feesResponse = await apiClient.get('/additional_fees/?page_size=100');
          const feeRows = feesResponse.data?.results || feesResponse.data || [];
          const targetStatus = isLogistiqueCoordinator ? 'EN_ATTENTE' : 'VERIFIE';
          const feesToReview = (Array.isArray(feeRows) ? feeRows : [])
            .filter((fee) => fee?.status === targetStatus);
          setPendingAdditionalFees(feesToReview);

          const restockStatus = isLogistiqueCoordinator ? 'PENDING_LOGISTICS' : 'PENDING_MANAGER';
          const restockResponse = await sessionService.supplierOrders({
            order_type_group: 'STOCK_RESTOCK',
            status: restockStatus,
            page_size: 10,
          });
          setPendingRestockOrders(restockResponse?.results || []);

          const recentDemandesResponse = await sessionService.supplierOrders({ page_size: 10 });
          setRecentDemandes((recentDemandesResponse?.results || []).slice(0, 10));
        }

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isResponsable, isLogistiqueCoordinator, isManager, isFournisseur, isComptable]);

  const monthlyRows = Array.isArray(accountingDashboard?.monthly) ? accountingDashboard.monthly : [];
  const currentMonthRow = monthlyRows.length > 0 ? monthlyRows[monthlyRows.length - 1] : null;

  const getCurrency = () => {
    return user?.centres_details?.[0]?.devise || 'EUR';
  };

  const toMoney = (value, currency = getCurrency()) => {
    return formatMoney(value, { locale: i18n.language, currency });
  };

  const renderSessionBudget = (session) => {
    const totalsByCurrency = session?.budget_totals_by_currency;

    if (totalsByCurrency && Object.keys(totalsByCurrency).length > 0) {
      return Object.entries(totalsByCurrency)
        .map(([currency, amount]) => toMoney(amount, currency))
        .join(' / ');
    }

    const rawBudget = session?.total_budget;
    if (typeof rawBudget === 'string' && /[A-Za-z]{3}/.test(rawBudget)) {
      return rawBudget;
    }

    return toMoney(rawBudget);
  };

  const getSessionStatusClass = (status) => {
    switch (status) {
      case 'VERIFICATION':
      case 'DEMANDE_MODIF':
      case 'DEMANDE_MODIF_MANAGER':
      case 'EN_ATTENTE':
      case 'PENDING_LOGISTICS':
      case 'PENDING_MANAGER':
        return 'status-pending';
      case 'NEW':
      case 'ACCEPTED':
      case 'SHIPPED':
      case 'VERIFIE':
        return 'status-approved';
      case 'VALIDEE':
      case 'VALIDE':
        return 'status-validated';
      case 'REFUSEE':
      case 'REFUSE':
        return 'status-rejected';
      case 'ANNULEE':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const getAdditionalFeeStatusClass = (status) => {
    switch (status) {
      case 'EN_ATTENTE':
        return 'status-pending';
      case 'VERIFIE':
        return 'status-approved';
      case 'VALIDE':
        return 'status-validated';
      case 'REFUSE':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const handleUpdateProfile = () => {
    setEditProfileOpen(true);
  };

  const handleResetPassword = () => {
    setChangePasswordOpen(true);
  };

  const handleScrollToProfile = () => {
    const el = document.getElementById('dashboard-profile');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loader"></div>
        <span>{t('dashboard.loading')}</span>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <EditProfileDialog
        isOpen={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        user={user}
        onProfileUpdated={refreshUser}
      />
      <ChangePasswordDialog
        isOpen={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onPasswordChanged={() => Promise.resolve()}
      />

      <div className="page-header">
        <div className="header-titles">
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <p className="welcome-text">
            {t('dashboard.welcome', { name: user?.first_name || '' })}
          </p>
        </div>
        <div className="dashboard-header-actions">
          <div className="header-date">
            {new Date().toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          <button type="button" className="dashboard-header-profile-btn" onClick={handleScrollToProfile}>
            <User size={16} />
            {t('dashboard.profile')}
          </button>
        </div>
      </div>

      <div className={`dashboard-layout${isComptable ? ' dashboard-layout-comptable' : ''}`}>
        <div className="dashboard-main">
          {/* Stats Grid */}
          <div className="stats-grid">
            {isAdmin && stats && (
              <>
                <StatCard
                  icon={Briefcase}
                  value={stats.total_centres}
                  label={t('dashboard.stats.centres')}
                  color="primary"
                />
                <StatCard
                  icon={Shield}
                  value={stats.total_responsables}
                  label={t('dashboard.stats.responsables')}
                  color="info"
                />
                <StatCard
                  icon={Calendar}
                  value={stats.ongoing_sessions}
                  label={t('dashboard.stats.ongoingSessions')}
                  color="success"
                />
              </>
            )}

            {isResponsable && stats && (
              <>
                <StatCard
                  icon={BookOpen}
                  value={stats.total_formations}
                  label={t('dashboard.stats.formations')}
                  color="primary"
                />
                <StatCard
                  icon={Calendar}
                  value={stats.total_sessions}
                  label={t('dashboard.stats.totalSessions')}
                  color="info"
                />
                <StatCard
                  icon={Clock}
                  value={stats.pending_validation}
                  label={t('status.session.EN_ATTENTE')}
                  color="warning"
                />
                <StatCard
                  icon={CheckCircle}
                  value={stats.validated_budget}
                  label={t('dashboard.stats.validatedBudget')}
                  color="success"
                />
              </>
            )}

            {isManager && stats && (
              <>
                <StatCard
                  icon={Clock}
                  value={stats.pending_validation}
                  label={t('status.session.VERIFIE')}
                  color="warning"
                />
                <StatCard
                  icon={CheckCircle}
                  value={stats.validated_budget}
                  label={t('dashboard.stats.validatedBudget')}
                  color="success"
                />
                <StatCard
                  icon={Calendar}
                  value={stats.total_sessions}
                  label={t('dashboard.stats.totalSessions')}
                  color="info"
                />
              </>
            )}

            {isLogistiqueCoordinator && stats && (
              <>
                <StatCard
                  icon={AlertCircle}
                  value={stats.pending_verification}
                  label={t('status.session.VERIFICATION')}
                  color="warning"
                />
                <StatCard
                  icon={Calendar}
                  value={stats.total_sessions}
                  label={t('dashboard.stats.totalSessions')}
                  color="info"
                />
              </>
            )}

            {isFournisseur && stats && (
              <>
                <StatCard
                  icon={ClipboardList}
                  value={stats.total_orders}
                  label={t('dashboard.stats.totalOrders')}
                  color="primary"
                />
                <StatCard
                  icon={Clock}
                  value={stats.pending_orders}
                  label={t('dashboard.stats.pendingOrders')}
                  color="warning"
                />
                <StatCard
                  icon={Package}
                  value={stats.total_products}
                  label={t('dashboard.stats.totalProducts')}
                  color="info"
                />
              </>
            )}
          </div>

          {isComptable && (
            <section className="pending-approvals accounting-monthly" style={{ marginTop: '0.5rem' }}>
              <div className="section-header" style={{ marginBottom: 0, paddingBottom: 0 }}>
                <h2 className="section-title">
                  <Euro size={20} />
                  Statistiques comptables (par mois)
                </h2>
              </div>

              {currentMonthRow && (
                <div className="stats-grid" style={{ marginTop: '0.75rem' }}>
                  <StatCard
                    icon={Calendar}
                    value={currentMonthRow.sessions_count}
                    label={'Sessions validées (mois)'}
                    color="info"
                  />
                  <StatCard
                    icon={Euro}
                    value={toMoney(currentMonthRow.budget_total)}
                    label={'Budget total (mois)'}
                    color="primary"
                  />
                  <StatCard
                    icon={Euro}
                    value={toMoney(currentMonthRow.budget_avg)}
                    label={'Budget moyen (mois)'}
                    color="success"
                  />
                </div>
              )}

              <div style={{ marginTop: '0.75rem', overflowX: 'auto', flex: 1, minHeight: 0 }}>
                <div style={{
                  height: '100%',
                  overflowY: 'auto',
                  border: '1px solid #E5E7EB',
                  borderRadius: 12,
                  background: '#FFFFFF',
                }}>
                  <table className="data-table" style={{ margin: 0, fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Mois</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Sessions</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Budget total</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Budget moyen</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Commandes</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Frais</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Total</th>
                      <th style={{ position: 'sticky', top: 0, background: '#F9FAFB', zIndex: 1, padding: '0.85rem 0.9rem' }}>Top fournisseurs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>Aucune donnée</td>
                      </tr>
                    ) : (
                      monthlyRows.map((row, idx) => (
                        <tr key={row.month} style={{ background: idx % 2 ? '#FCFCFC' : 'transparent' }}>
                          <td style={{ fontWeight: 900, padding: '0.85rem 0.9rem' }}>{row.month}</td>
                          <td style={{ padding: '0.85rem 0.9rem' }}>{row.sessions_count}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '0.85rem 0.9rem' }}>{toMoney(row.budget_total)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '0.85rem 0.9rem' }}>{toMoney(row.budget_avg)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '0.85rem 0.9rem' }}>{toMoney(row.orders_total)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', padding: '0.85rem 0.9rem' }}>{toMoney(row.fees_total)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 950, padding: '0.85rem 0.9rem' }}>{toMoney(row.grand_total)}</td>
                          <td style={{ padding: '0.85rem 0.9rem' }}>
                            {(row.top_suppliers || []).length === 0 ? (
                              '-'
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                {(row.top_suppliers || []).slice(0, 3).map((s) => (
                                  <div key={s.supplier_id || s.supplier_name} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{s.supplier_name || '-'}</span>
                                    <span style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{toMoney(s.orders_total)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {(isLogistiqueCoordinator || isManager) && (
            <section className="recent-sessions">
              <div className="section-header">
                <h2 className="section-title">
                  <ClipboardList size={20} />
                  Dernières demandes
                </h2>
                <Link to="/sessions" className="view-all-link-small">
                  Voir dans Sessions
                </Link>
              </div>

              {recentDemandes.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucune demande</h4>
                  <p>Aucune demande récente à afficher.</p>
                </div>
              ) : (
                <div className="recent-sessions-list">
                  {recentDemandes.map((order) => (
                    <Link to={`/mes-demandes/${order.order_id}`} key={order.order_id} className="recent-session-item">
                      <div className="session-info">
                        <h4 className="session-title">
                          {order.order_type_label || order.formation_title || 'Demande'}
                        </h4>
                        <div className="session-meta">
                          <span className="session-date">
                            <Calendar size={14} />
                            {order.created_at ? new Date(order.created_at).toLocaleDateString(i18n.language) : '-'}
                          </span>
                          <span className="session-center">
                            <Building2 size={14} />
                            {order.centre_name || '-'}
                          </span>
                          <span className="session-center">
                            <Truck size={14} />
                            {order.supplier_name || '-'}
                          </span>
                        </div>
                      </div>
                      <span className={`session-status-badge ${getSessionStatusClass(order.status)}`}>
                        {order.status_display || t(`demandes.status.${order.status}`)}
                      </span>
                      <ArrowRight size={18} className="session-arrow" />
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Recent Orders Section - Only for Supplier */}
          {isFournisseur && stats?.recent_orders?.length > 0 && (
            <section className="recent-sessions">
              <div className="section-header">
                <h2 className="section-title">
                  <Truck size={20} />
                  {t('dashboard.recentOrders.title') || 'Dernières commandes reçues'}
                </h2>
                <Link to="/mes-demandes" className="view-all-link-small">
                  {t('common.viewAll')}
                </Link>
              </div>
              <div className="recent-sessions-list">
                {stats.recent_orders.map((order, idx) => {
                  const orderId = order?.order_id || order?.id || order?.supplier_order_id;

                  const content = (
                    <>
                      <div className="session-info">
                        <h4 className="session-title">{order.formation_title}</h4>
                        <div className="session-meta">
                          <span className="session-date">
                            <Calendar size={14} />
                            {order.validated_at ? new Date(order.validated_at).toLocaleDateString(i18n.language) : '-'}
                          </span>
                          <span className="session-center">
                            <Building2 size={14} />
                            {order.centre_name || '-'}
                          </span>
                        </div>
                      </div>
                      <span className="session-status-badge status-validated">
                        {t('status.session.VALIDEE')}
                      </span>
                      <ArrowRight size={18} className="session-arrow" />
                    </>
                  );

                  if (!orderId) {
                    return (
                      <div
                        key={`recent-order-missing-id-${idx}`}
                        className="recent-session-item"
                        style={{ cursor: 'not-allowed', opacity: 0.65 }}
                        title="Commande introuvable"
                      >
                        {content}
                      </div>
                    );
                  }

                  return (
                    <Link to={`/mes-demandes/${orderId}`} key={orderId} className="recent-session-item">
                      {content}
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Sessions pending validation - Only for Manager */}
          {isManager && (
            <section className="pending-approvals">
              <div className="section-header">
                <h2 className="section-title">
                  <Clock size={20} />
                  Sessions en attente de validation
                </h2>
                {pendingValidationSessions.length > 0 && (
                  <span className="pending-count">{pendingValidationSessions.length}</span>
                )}
              </div>

              {pendingValidationSessions.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucune session</h4>
                  <p>Aucune session en attente de validation.</p>
                </div>
              ) : (
                <>
                  <div className="approvals-list">
                    {pendingValidationSessions.slice(0, 10).map((session) => (
                      <Link to={`/sessions/${session.id}`} key={session.id} className="approval-item">
                        <div className="approval-info">
                          <h4 className="approval-title">{session.formation_title}</h4>
                          <div className="approval-meta">
                            <span className="approval-trainer">
                              <User size={14} />
                              {session.trainer_name}
                            </span>
                            <span className="approval-students">
                              <UsersIcon size={14} />
                              {t('dashboard.participantsCount', { count: session.student_count || 0 })}
                            </span>
                          </div>
                        </div>
                        <div className="approval-budget">
                          <Euro size={16} />
                          <span>{renderSessionBudget(session)}</span>
                        </div>
                        <ArrowRight size={18} className="approval-arrow" />
                      </Link>
                    ))}
                  </div>
                  {pendingValidationSessions.length > 10 && (
                    <Link to="/sessions" className="view-all-link">
                      Voir toutes les sessions ({pendingValidationSessions.length})
                    </Link>
                  )}
                </>
              )}
            </section>
          )}

          {/* Recent Sessions Section - Only for Admin */}
          {isAdmin && stats?.recent_sessions?.length > 0 && (
            <section className="recent-sessions">
              <div className="section-header">
                <h2 className="section-title">
                  <ClipboardList size={20} />
                  {t('dashboard.recentSessions.adminTitle')}
                </h2>
                <Link to="/sessions" className="view-all-link-small">
                  {t('common.viewAll')}
                </Link>
              </div>
              <div className="recent-sessions-list">
                {stats.recent_sessions.slice(0, 10).map((session) => (
                  <Link to={`/sessions/${session.id}`} key={session.id} className="recent-session-item">
                    <div className="session-info">
                      <h4 className="session-title">{session.formation_title}</h4>
                      <div className="session-meta">
                        <span className="session-date">
                          <Calendar size={14} />
                          {session.start_date ? new Date(session.start_date).toLocaleDateString(i18n.language) : '-'}
                        </span>
                        <span className="session-students">
                          <UsersIcon size={14} />
                          {t('dashboard.participantsCount', { count: session.student_count || 0 })}
                        </span>
                      </div>
                    </div>
                    <span className={`session-status-badge ${getSessionStatusClass(session.status)}`}>
                      {t(`status.session.${session.status}`)}
                    </span>
                    <ArrowRight size={18} className="session-arrow" />
                  </Link>
                ))}
              </div>
            </section>
          )}


          {/* My Recent Sessions Section - Only for Responsable */}
          {isResponsable && (
            <section className="pending-approvals">
              <div className="section-header">
                <h2 className="section-title">
                  <Calendar size={20} />
                  Mes 10 dernières sessions
                </h2>
                {pendingSessions.length > 0 && (
                  <span className="pending-count">{pendingSessions.length}</span>
                )}
              </div>

              {pendingSessions.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucune session</h4>
                  <p>Vous n'avez pas encore créé de session.</p>
                </div>
              ) : (
                <>
                  <div className="approvals-list">
                    {pendingSessions.slice(0, 10).map((session) => (
                      <Link to={`/sessions/${session.id}`} key={session.id} className="approval-item">
                        <div className="approval-info">
                          <h4 className="approval-title">{session.formation_title}</h4>
                          <div className="approval-meta">
                            <span className="approval-trainer">
                              <User size={14} />
                              {session.trainer_name}
                            </span>
                            <span className="approval-students">
                              <UsersIcon size={14} />
                              {t('dashboard.participantsCount', { count: session.student_count || 0 })}
                            </span>
                          </div>
                        </div>
                        <div className="approval-budget">
                          <Euro size={16} />
                          <span>{renderSessionBudget(session)}</span>
                        </div>
                        <ArrowRight size={18} className="approval-arrow" />
                      </Link>
                    ))}
                  </div>
                  {pendingSessions.length > 10 && (
                    <Link to="/sessions" className="view-all-link">
                      Voir toutes mes sessions ({pendingSessions.length})
                    </Link>
                  )}
                </>
              )}
            </section>
          )}

          {/* Sessions pending verification - Only for Logistique Coordinator */}
          {isLogistiqueCoordinator && (
            <section className="pending-approvals">
              <div className="section-header">
                <h2 className="section-title">
                  <AlertCircle size={20} />
                  Sessions en attente de vérification
                </h2>
                {verificationSessions.length > 0 && (
                  <span className="pending-count">{verificationSessions.length}</span>
                )}
              </div>

              {verificationSessions.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucune session</h4>
                  <p>Aucune session en attente de vérification.</p>
                </div>
              ) : (
                <>
                  <div className="approvals-list">
                    {verificationSessions.slice(0, 10).map((session) => (
                      <Link to={`/sessions/${session.id}`} key={session.id} className="approval-item">
                        <div className="approval-info">
                          <h4 className="approval-title">{session.formation_title}</h4>
                          <div className="approval-meta">
                            <span className="approval-trainer">
                              <User size={14} />
                              {session.trainer_name}
                            </span>
                            <span className="approval-students">
                              <UsersIcon size={14} />
                              {t('dashboard.participantsCount', { count: session.student_count || 0 })}
                            </span>
                          </div>
                        </div>
                        <div className="approval-budget">
                          <Euro size={16} />
                          <span>{renderSessionBudget(session)}</span>
                        </div>
                        <ArrowRight size={18} className="approval-arrow" />
                      </Link>
                    ))}
                  </div>
                  {verificationSessions.length > 10 && (
                    <Link to="/sessions" className="view-all-link">
                      Voir toutes les sessions ({verificationSessions.length})
                    </Link>
                  )}
                </>
              )}
            </section>
          )}

          {(isLogistiqueCoordinator || isManager) && (
            <section className="pending-approvals">
              <div className="section-header">
                <h2 className="section-title">
                  <Package size={20} />
                  {isLogistiqueCoordinator ? 'Réapprovisionnements à vérifier' : 'Réapprovisionnements à valider'}
                </h2>
                {pendingRestockOrders.length > 0 && (
                  <span className="pending-count">{pendingRestockOrders.length}</span>
                )}
              </div>

              {pendingRestockOrders.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucune demande de réapprovisionnement</h4>
                  <p>
                    {isLogistiqueCoordinator
                      ? 'Aucune demande stock en attente de vérification logistique.'
                      : 'Aucune demande stock en attente de validation manager.'}
                  </p>
                </div>
              ) : (
                <div className="approvals-list">
                  {pendingRestockOrders.map((order) => (
                    <Link to={`/mes-demandes/${order.order_id}`} key={order.order_id} className="approval-item">
                      <div className="approval-info">
                        <h4 className="approval-title">{order.supplier_name || 'Fournisseur inconnu'}</h4>
                        <div className="approval-meta">
                          <span className="approval-trainer">
                            <Building2 size={14} />
                            {order.centre_name || 'Centre inconnu'}
                          </span>
                          <span className={`session-status-badge ${getSessionStatusClass(order.status)}`}>
                            {order.status_display || t(`demandes.status.${order.status}`)}
                          </span>
                        </div>
                      </div>
                      <div className="approval-budget">
                        <Euro size={16} />
                        <span>{toMoney(order.total_devis || 0, order.supplier_devise || order.centre_devise)}</span>
                      </div>
                      <ArrowRight size={18} className="approval-arrow" />
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}

          {(isLogistiqueCoordinator || isManager) && (
            <section className="pending-approvals">
              <div className="section-header">
                <h2 className="section-title">
                  <Receipt size={20} />
                  {isLogistiqueCoordinator ? 'Frais additionnels à vérifier' : 'Frais additionnels à valider'}
                </h2>
                {pendingAdditionalFees.length > 0 && (
                  <span className="pending-count">{pendingAdditionalFees.length}</span>
                )}
              </div>

              {pendingAdditionalFees.length === 0 ? (
                <div className="empty-approvals">
                  <CheckCircle size={48} className="empty-icon" />
                  <h4>Aucun frais additionnel</h4>
                  <p>
                    {isLogistiqueCoordinator
                      ? 'Aucun frais additionnel en attente de vérification.'
                      : 'Aucun frais additionnel en attente de validation.'}
                  </p>
                </div>
              ) : (
                <div className="approvals-list">
                  {pendingAdditionalFees.map((fee) => (
                    <Link to={`/sessions/${fee.session}#additional-fees`} key={fee.id} className="approval-item">
                      <div className="approval-info">
                        <h4 className="approval-title">{fee.label || 'Frais additionnel'}</h4>
                        <div className="approval-meta">
                          <span className="approval-trainer">
                            <Building2 size={14} />
                            {fee.supplier_name || 'Fournisseur inconnu'}
                          </span>
                          <span className={`session-status-badge ${getAdditionalFeeStatusClass(fee.status)}`}>
                            {fee.status_display || fee.status}
                          </span>
                        </div>
                      </div>
                      <div className="approval-budget">
                        <Euro size={16} />
                        <span>{toMoney(fee.amount)}</span>
                      </div>
                      <ArrowRight size={18} className="approval-arrow" />
                    </Link>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="dashboard-sidebar">
          {/* Profile Card */}
          <div id="dashboard-profile" className="profile-card">
            <div className="profile-header">
              <div className="profile-avatar">
                <User size={32} />
              </div>
              <h3 className="profile-name">{user?.first_name} {user?.last_name}</h3>
              <span className="profile-role">
                {user?.role === 'ADMIN' && <><Shield size={14} /> {t('roles.admin')}</>}
                {(user?.role === 'BU_COORDINATOR' || user?.role === 'RESPONSABLE') && <><Shield size={14} /> {t('roles.bu_coordinator')}</>}
                {user?.role === 'LOGISTIQUE_COORDINATOR' && <><Shield size={14} /> {t('roles.logistique_coordinator')}</>}
                {user?.role === 'MANAGER' && <><Shield size={14} /> {t('roles.manager')}</>}
                {user?.role === 'COMPTABLE' && <><Shield size={14} /> {t('roles.comptable')}</>}
                {user?.role === 'FOURNISSEUR' && <><Truck size={14} /> {t('roles.fournisseur') || 'Fournisseur'}</>}
                {user?.role === 'ETUDIANT' && <><User size={14} /> {t('roles.student')}</>}
              </span>
            </div>

            <div className="profile-info">
              <div className="info-row">
                <Mail size={16} />
                <span>{user?.email}</span>
              </div>
              {user?.centre_name && (
                <div className="info-row">
                  <Briefcase size={16} />
                  <span>{user.centre_name}</span>
                </div>
              )}
            </div>

            <div className="profile-actions">
              <button className="profile-btn profile-btn-primary" onClick={handleUpdateProfile}>
                <Edit3 size={18} />
                {t('dashboard.profileActions.editProfile')}
              </button>
              <button className="profile-btn profile-btn-secondary" onClick={handleResetPassword}>
                <Key size={18} />
                {t('dashboard.profileActions.resetPassword')}
              </button>
            </div>
          </div>

          {/* Quick Actions - Below Profile */}
          <section className="quick-actions-sidebar">
            <h3 className="sidebar-section-title">{t('dashboard.quickActions.title')}</h3>
            <div className="quick-actions-list">
              <Link to="/formations" className="quick-action-item">
                <BookOpen size={20} />
                <span>{t('dashboard.quickActions.catalog')}</span>
              </Link>

              {isResponsable && (
                <Link to="/sessions/new" className="quick-action-item quick-action-highlight">
                  <PlusCircle size={20} />
                  <span>{t('dashboard.quickActions.newSession')}</span>
                </Link>
              )}

              <Link to="/sessions" className="quick-action-item">
                <ClipboardList size={20} />
                <span>{t('dashboard.quickActions.sessionsFollowUp')}</span>
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, value, label, color }) => (
  <div className={`stat-card stat-card-${color}`}>
    <div className="stat-icon-wrapper">
      <Icon size={24} />
    </div>
    <div className="stat-info">
      <h3 className="stat-value">{value}</h3>
      <p className="stat-label">{label}</p>
    </div>
  </div>
);

export default DashboardPage;
